'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { TUTORIAL_TAX_TOKEN_DECIMALS, TUTORIAL_VIEWER } from './tutorial-board';
import { clientChain } from '@/lib/viem-client';
import type { LinkActiveWalletResult } from '@/hooks/use-wallet';

/** Stable demo balance: 1,000 USDC (6 dp) so the user never sees an
 *  insufficient-funds path during the scripted buyout. */
const TUTORIAL_BALANCE = 1_000n * 10n ** BigInt(TUTORIAL_TAX_TOKEN_DECIMALS);
/** Same magnitude as the USDC balance, but in 18 dp for any code path
 *  that reaches for the TAKEOVER balance. */
const TUTORIAL_TAKEOVER_BALANCE = 1_000n * 10n ** 18n;

const TUTORIAL_WALLET_OBJECT = {
  address: TUTORIAL_VIEWER.address as `0x${string}`,
  walletClientType: 'tutorial' as const,
  // Privy/wagmi never call these in tutorial mode but the embedded shape
  // hints at where the player would normally reach a provider. Includes
  // a no-op `switchChain` so transaction-queue code paths that gate on
  // `walletClientType === 'privy'` (embedded) but still type-check the
  // method for external wallets compile cleanly.
  getEthereumProvider: async () => null,
  switchChain: async (_chainId: number) => {},
};

/**
 * Subset of `useWallet()` that the lobby + drawers actually consume. We
 * keep the surface explicit so adding a new field to `useWallet` doesn't
 * silently leak undefined into tutorial code.
 */
export interface TutorialWalletShape {
  ready: boolean;
  authenticated: boolean;
  isConnected: boolean;
  isAddressLinked: boolean;
  hasExternalWallet: boolean;
  isEmbeddedWallet: boolean;
  isLoading: false;
  connectBusy: boolean;
  address: `0x${string}`;
  /**
   * Every wallet linked to the (tutorial) viewer's session, lowercased.
   * Mirrors `useWallet().linkedAddresses` so consumers like
   * `useGridPage` can derive the broader "viewer wallets" set the
   * same way in tutorial mode as in production. Tutorial only ever
   * owns the synthetic `TUTORIAL_VIEWER.address`.
   */
  linkedAddresses: ReadonlySet<string>;
  embeddedAddress: `0x${string}` | null;
  walletsReady: boolean;
  signInActiveWallet: () => Promise<LinkActiveWalletResult>;
  displayName: string;
  avatarUrl: string;
  usdcBalance: bigint;
  refetchUsdcBalance: () => void;
  takeoverBalance: bigint;
  refetchTakeoverBalance: () => void;
  isBalancesLoading: boolean;
  refreshAllBalances: () => Promise<void>;
  points: number;
  connectWallet: () => void;
  logout: () => Promise<void>;
  fundWallet: (options?: { amount?: string; asset?: 'USDC' | 'native-currency' }) => Promise<void>;
  exportWallet: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  linkTwitter: () => void;
  linkFarcaster: () => void;
  isLinkingAccount: boolean;
  wallet: typeof TUTORIAL_WALLET_OBJECT;
  user: null;
  currentChainId: typeof clientChain.id | null;
  expectedChainId: typeof clientChain.id;
  expectedNetworkName: string;
  isCorrectNetwork: boolean;
  hasCheckedNetwork: boolean;
  switchNetwork: () => Promise<void>;
}

const TutorialWalletContext = createContext<TutorialWalletShape | null>(null);

export function TutorialWalletProvider({ children }: { children: ReactNode }) {
  const value = useMemo<TutorialWalletShape>(() => {
    const noop = () => {};
    const asyncNoop = async () => {};
    return {
      ready: true,
      authenticated: true,
      isConnected: true,
      isAddressLinked: true,
      hasExternalWallet: false,
      isEmbeddedWallet: true,
      isLoading: false,
      connectBusy: false,
      address: TUTORIAL_VIEWER.address as `0x${string}`,
      linkedAddresses: new Set<string>([
        (TUTORIAL_VIEWER.address as string).toLowerCase(),
      ]),
      embeddedAddress: TUTORIAL_VIEWER.address as `0x${string}`,
      // Tutorial mode short-circuits the sign-in flow — the shim's
      // synthetic wallet is "always linked" so the chat sign-in gate
      // never fires during a scripted run.
      walletsReady: true,
      signInActiveWallet: async () => ({ ok: true, reason: 'already-linked' as const }),
      displayName: TUTORIAL_VIEWER.name,
      avatarUrl: TUTORIAL_VIEWER.avatarUrl,
      usdcBalance: TUTORIAL_BALANCE,
      refetchUsdcBalance: noop,
      takeoverBalance: TUTORIAL_TAKEOVER_BALANCE,
      refetchTakeoverBalance: noop,
      isBalancesLoading: false,
      refreshAllBalances: asyncNoop,
      points: 0,
      connectWallet: noop,
      logout: asyncNoop,
      fundWallet: asyncNoop,
      exportWallet: asyncNoop,
      getAccessToken: async () => null,
      linkTwitter: noop,
      linkFarcaster: noop,
      isLinkingAccount: false,
      wallet: TUTORIAL_WALLET_OBJECT,
      user: null,
      currentChainId: clientChain.id,
      expectedChainId: clientChain.id,
      expectedNetworkName: clientChain.name,
      isCorrectNetwork: true,
      hasCheckedNetwork: true,
      switchNetwork: asyncNoop,
    };
  }, []);

  return (
    <TutorialWalletContext.Provider value={value}>
      {children}
    </TutorialWalletContext.Provider>
  );
}

/** Returns the tutorial wallet shim if the consumer is mounted underneath
 *  a `TutorialWalletProvider`, otherwise `null`. The real `useWallet()`
 *  early-returns on a non-null result. */
export function useTutorialWalletOptional(): TutorialWalletShape | null {
  return useContext(TutorialWalletContext);
}
